import { Map } from 'immutable';

// @ts-expect-error - TypedMap extends Map with incompatible type parameters for better type safety
export interface TypedMap<T> extends Map<keyof T, T[keyof T]> {
    filter<K extends keyof T>(
        predicate: (value: T[K], key: K) => boolean,
    ): TypedMap<T>;

    get<K extends keyof T, U = never>(keyPath: K, notSetValue?: U): T[K];

    has<K extends keyof T>(key: K): boolean;
    has(key: string): boolean;

    set<K extends keyof T>(key: K, value: T[K]): TypedMap<T>;

    reduce<R>(
        reducer: (
            reduction: R,
            value: T[keyof T],
            key: keyof T,
            iter: this,
        ) => R,
        initialReduction: R,
    ): R;

    getIn<K1 extends keyof T, U>(path: [K1], notSetValue?: U): T[K1];

    getIn<K1 extends keyof T, K2 extends keyof ReturnMap<T[K1]>, U>(
        path: [K1, K2],
        notSetValue?: U,
    ): ReturnMap<T[K1]>[K2];

    getIn<
        K1 extends keyof T,
        K2 extends keyof ReturnMap<T[K1]>,
        K3 extends keyof ReturnMap<ReturnMap<T[K1]>[K2]>,
        U = never,
    >(
        path: [K1, K2, K3],
        notSetValue?: U,
    ): ReturnMap<ReturnMap<T[K1]>[K2]>[K3];

    getIn<
        K1 extends keyof T,
        K2 extends keyof ReturnMap<T[K1]>,
        K3 extends keyof ReturnMap<ReturnMap<T[K1]>[K2]>,
        K4 extends keyof ReturnMap<ReturnMap<ReturnMap<T[K1]>[K2]>[K3]>,
        U = never,
    >(
        path: [K1, K2, K3, K4],
        notSetValue?: U,
    ): ReturnMap<ReturnMap<ReturnMap<T[K1]>[K2]>[K3]>[K4];

    getIn<
        K1 extends keyof T,
        K2 extends keyof ReturnMap<T[K1]>,
        K3 extends keyof ReturnMap<ReturnMap<T[K1]>[K2]>,
        K4 extends keyof ReturnMap<ReturnMap<ReturnMap<T[K1]>[K2]>[K3]>,
        K5 extends keyof ReturnMap<
            ReturnMap<ReturnMap<ReturnMap<T[K1]>[K2]>[K3]>[K4]
        >,
        U = never,
    >(
        path: [K1, K2, K3, K4, K5],
        notSetValue?: U,
    ): ReturnMap<ReturnMap<ReturnMap<ReturnMap<T[K1]>[K2]>[K3]>[K4]>[K5];

    getIn<
        K1 extends keyof T,
        K2 extends keyof ReturnMap<T[K1]>,
        K3 extends keyof ReturnMap<ReturnMap<T[K1]>[K2]>,
        K4 extends keyof ReturnMap<ReturnMap<ReturnMap<T[K1]>[K2]>[K3]>,
        K5 extends keyof ReturnMap<
            ReturnMap<ReturnMap<ReturnMap<T[K1]>[K2]>[K3]>[K4]
        >,
        K6 extends keyof ReturnMap<
            ReturnMap<ReturnMap<ReturnMap<ReturnMap<T[K1]>[K2]>[K3]>[K4]>[K5]
        >,
        U = never,
    >(
        path: [K1, K2, K3, K4, K5, K6],
        notSetValue?: U,
    ): ReturnMap<
        ReturnMap<ReturnMap<ReturnMap<ReturnMap<T[K1]>[K2]>[K3]>[K4]>[K5]
    >[K6];

    getIn<
        K1 extends keyof T,
        K2 extends keyof ReturnMap<T[K1]>,
        K3 extends keyof ReturnMap<ReturnMap<T[K1]>[K2]>,
        K4 extends keyof ReturnMap<ReturnMap<ReturnMap<T[K1]>[K2]>[K3]>,
        K5 extends keyof ReturnMap<
            ReturnMap<ReturnMap<ReturnMap<T[K1]>[K2]>[K3]>[K4]
        >,
        K6 extends keyof ReturnMap<
            ReturnMap<ReturnMap<ReturnMap<ReturnMap<T[K1]>[K2]>[K3]>[K4]>[K5]
        >,
        K7 extends keyof ReturnMap<
            ReturnMap<
                ReturnMap<
                    ReturnMap<ReturnMap<ReturnMap<T[K1]>[K2]>[K3]>[K4]
                >[K5]
            >[K6]
        >,
        U = never,
    >(
        path: [K1, K2, K3, K4, K5, K6, K7],
        notSetValue?: U,
    ): ReturnMap<
        ReturnMap<
            ReturnMap<ReturnMap<ReturnMap<ReturnMap<T[K1]>[K2]>[K3]>[K4]>[K5]
        >[K6]
    >[K7];

    mapEntries<KM extends string, VM, K extends keyof T>(
        mapper: (entry: [K, T[K]], index: number, iter: this) => [KM, VM],
        context?: unknown,
    ): TypedMap<Record<KM, VM>>;
}

type ReturnMap<T> = T extends TypedMap<infer U> ? U : T;

/**
 * Creates an empty TypedMap without requiring 'as unknown as' cast.
 * Use this as the initial value for reduce operations.
 */
export function createTypedMap<T>(): TypedMap<T> {
    return Map() as unknown as TypedMap<T>;
}
